import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { StatCard } from "../components/shared/StatCard";
import { Toggle } from "../components/shared/Toggle";
import { Modal } from "../components/shared/Modal";
import { Spinner } from "../components/shared/Spinner";
import { dealsAPI } from "../services/api";
import { fmt } from "../components/shared/fmt";
import { useTitle } from "../hooks/useTitle";

export const DealsPage: React.FC = () => {
	useTitle("Deals & Promos");
	const qc = useQueryClient();
	const { data, isLoading } = useQuery({
		queryKey: ["deals"],
		queryFn: () => dealsAPI.getAll(),
	});
	const deals = data?.data || [];
	const [modal, setModal] = useState(false);
	const [sel, setSel] = useState<any>(null);
	const [form, setForm] = useState({
		name: "",
		code: "",
		discount: "",
		discountType: "percent",
		description: "",
		emoji: "🎁",
		color: "#06a384",
		minOrderValue: "",
	});

	const saveMut = useMutation({
		mutationFn: (d: any) =>
			sel ? dealsAPI.update(sel.id, d) : dealsAPI.create(d),
		onSuccess: () => {
			toast.success(sel ? "Updated" : "Created");
			qc.invalidateQueries({ queryKey: ["deals"] });
			setModal(false);
		},
	});
	const toggleMut = useMutation({
		mutationFn: dealsAPI.toggle,
		onSuccess: () => qc.invalidateQueries({ queryKey: ["deals"] }),
	});
	const delMut = useMutation({
		mutationFn: dealsAPI.delete,
		onSuccess: () => {
			toast.success("Deleted");
			qc.invalidateQueries({ queryKey: ["deals"] });
		},
	});

	const openNew = () => {
		setSel(null);
		setForm({
			name: "",
			code: "",
			discount: "",
			discountType: "percent",
			description: "",
			emoji: "🎁",
			color: "#06a384",
			minOrderValue: "",
		});
		setModal(true);
	};
	const openEdit = (d: any) => {
		setSel(d);
		setForm({
			name: d.name,
			code: d.code,
			discount: String(d.discount),
			discountType: d.discountType,
			description: d.description,
			emoji: d.emoji,
			color: d.color || "#06a384",
			minOrderValue: d.minOrderValue ? String(d.minOrderValue) : "",
		});
		setModal(true);
	};

	if (isLoading) return <Spinner />;

	return (
		<div>
			<div className="page-title">Deals & Promotions</div>
			<div className="page-sub">
				Manage discount codes, bundles, and special offers
			</div>

			<div className="stat-grid">
				<StatCard
					label="Active Deals"
					value={deals.filter((d: any) => d.active).length}
					cls="green"
					icon="🏷️"
				/>
				<StatCard
					label="Total Uses"
					value={deals.reduce((s: number, d: any) => s + d.uses, 0)}
					icon="📊"
				/>
				<StatCard
					label="Revenue"
					value={fmt(
						deals.reduce((s: number, d: any) => s + (d.revenue || 0), 0),
					)}
					cls="green"
					icon="💰"
				/>
				<StatCard
					label="Most Used"
					value={
						[...deals].sort((a: any, b: any) => b.uses - a.uses)[0]?.name || "—"
					}
					cls="amber"
					icon="⭐"
				/>
			</div>

			<div className="card">
				<div className="card-title">
					All Deals{" "}
					<button className="btn-primary" onClick={openNew}>
						+ New Deal
					</button>
				</div>
				<table className="data-table">
					<thead>
						<tr>
							<th>Deal</th>
							<th>Code</th>
							<th>Type</th>
							<th>Discount</th>
							<th>Uses</th>
							<th>Revenue</th>
							<th>Active</th>
							<th>Actions</th>
						</tr>
					</thead>
					<tbody>
						{deals.map((d: any) => (
							<tr key={d.id}>
								<td>
									<div
										style={{ display: "flex", alignItems: "center", gap: 8 }}>
										<span style={{ fontSize: 22 }}>{d.emoji}</span>
										<div>
											<div style={{ fontWeight: 700 }}>{d.name}</div>
											<div style={{ fontSize: 11, color: "var(--muted)" }}>
												{d.description}
											</div>
										</div>
									</div>
								</td>
								<td>
									<code
										style={{
											background: "var(--card2)",
											padding: "3px 8px",
											borderRadius: 5,
											fontSize: 12,
											fontWeight: 700,
											border: "1px dashed var(--border2)",
										}}>
										{d.code}
									</code>
								</td>
								<td>
									<span className="tag tag-blue">{d.type}</span>
								</td>
								<td
									style={{
										fontWeight: 800,
										fontSize: 15,
										color: "var(--amber)",
									}}>
									{d.discountType === "percent"
										? `${d.discount}%`
										: fmt(d.discount)}
								</td>
								<td style={{ fontWeight: 600 }}>{d.uses}</td>
								<td className="text-accent">
									{d.revenue > 0 ? fmt(d.revenue) : "—"}
								</td>
								<td>
									<Toggle
										on={d.active}
										onChange={() => toggleMut.mutate(d.id)}
									/>
								</td>
								<td>
									<div style={{ display: "flex", gap: 5 }}>
										<button
											className="btn-secondary"
											onClick={() => openEdit(d)}>
											✏️
										</button>
										<button
											className="btn-secondary btn-danger"
											onClick={() => {
												if (window.confirm("Delete?")) delMut.mutate(d.id);
											}}>
											🗑
										</button>
									</div>
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>

			{modal && (
				<Modal
					title={sel ? "Edit Deal" : "Create Deal"}
					onClose={() => setModal(false)}
					onConfirm={() =>
						saveMut.mutate({
							name: form.name,
							code: form.code.toUpperCase(),
							discount: parseFloat(form.discount) || 0,
							discountType: form.discountType,
							description: form.description,
							emoji: form.emoji,
							color: form.color,
							minOrderValue: form.minOrderValue
								? parseFloat(form.minOrderValue)
								: undefined,
						})
					}
					confirmText={sel ? "Save" : "Create"}>
					<div className="form-row cols-2">
						<div className="form-group">
							<label className="form-label">Deal Name</label>
							<input
								className="form-input"
								value={form.name}
								onChange={(e) =>
									setForm((f) => ({ ...f, name: e.target.value }))
								}
							/>
						</div>
						<div className="form-group">
							<label className="form-label">Promo Code</label>
							<input
								className="form-input"
								value={form.code}
								onChange={(e) =>
									setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))
								}
							/>
						</div>
					</div>
					<div className="form-row cols-2">
						<div className="form-group">
							<label className="form-label">Discount Value</label>
							<input
								className="form-input"
								type="number"
								value={form.discount}
								onChange={(e) =>
									setForm((f) => ({ ...f, discount: e.target.value }))
								}
							/>
						</div>
						<div className="form-group">
							<label className="form-label">Type</label>
							<select
								className="form-select"
								value={form.discountType}
								onChange={(e) =>
									setForm((f) => ({ ...f, discountType: e.target.value }))
								}>
								<option value="percent">Percentage (%)</option>
								<option value="fixed">Fixed (KWD)</option>
							</select>
						</div>
					</div>
					<div className="form-row cols-2">
						<div className="form-group">
							<label className="form-label">Emoji</label>
							<input
								className="form-input"
								value={form.emoji}
								onChange={(e) =>
									setForm((f) => ({ ...f, emoji: e.target.value }))
								}
							/>
						</div>
						<div className="form-group">
							<label className="form-label">Min Order (KWD)</label>
							<input
								className="form-input"
								type="number"
								step="0.001"
								value={form.minOrderValue}
								onChange={(e) =>
									setForm((f) => ({ ...f, minOrderValue: e.target.value }))
								}
								placeholder="Optional"
							/>
						</div>
					</div>
					<div className="form-group">
						<label className="form-label">Description</label>
						<input
							className="form-input"
							value={form.description}
							onChange={(e) =>
								setForm((f) => ({ ...f, description: e.target.value }))
							}
						/>
					</div>
				</Modal>
			)}
		</div>
	);
};
